/* eslint-disable import/no-named-as-default-member */
/* eslint-disable import/no-named-as-default */
import ReactDOM from 'react-dom';
import React from 'react';
import 'promise-polyfill';
// eslint-disable-next-line
import registerServiceWorker from 'Utils/pwa';
import initStore from 'App/initStore';
import App from 'App/app.jsx';
import {
    applyEverymanSessionCookieToLocalStorage,
    checkAndSetEndpointFromUrl,
    isEverymanAuthLogFull,
    isEverymanDeploymentHost,
} from '@deriv/shared';
import { processAuthTokensFromUrl, checkLogoutParam } from '@deriv/utils';
import AppNotificationMessages from './App/Containers/app-notification-messages.jsx';
import { AnalyticsInitializer } from 'Utils/Analytics';
import { startIframeBridge } from './App/iframe-bridge';

AnalyticsInitializer();
if (
    !!window?.localStorage.getItem?.('debug_service_worker') || // To enable local service worker related development
    (!window.location.hostname.startsWith('localhost') && !/binary\.sx/.test(window.location.hostname)) ||
    window.location.hostname === 'deriv-app.binary.sx'
) {
    registerServiceWorker();
}

const has_endpoint_url = checkAndSetEndpointFromUrl();

// if has endpoint url, APP will be redirected
if (!has_endpoint_url) {
    const initApp = () => {
        const queryParams = new URLSearchParams(window.location.search || '');

        const everymanSessionHydrated =
            isEverymanDeploymentHost() && applyEverymanSessionCookieToLocalStorage();

        // Everyman: parent-domain session cookie hydrates LS; never legacy URL tokens on this domain.
        // Elsewhere: optional URL token bootstrap for upstream Deriv flows.
        const isBridgeMode =
            queryParams.get('bridge') === 'postmessage' ||
            (typeof window !== 'undefined' && window.parent !== window);

        const shouldLogout = checkLogoutParam();

        if (!isBridgeMode && !shouldLogout && !isEverymanDeploymentHost()) {
            processAuthTokensFromUrl();
        }

        const root_store = initStore(AppNotificationMessages);

        if (isBridgeMode) {
            startIframeBridge({
                onAuth: async () => {
                    // Everyman subdomains: `client.init` already ran in `initStore` after session-cookie hydrate.
                    if (everymanSessionHydrated) {
                        return;
                    }
                    // Non-Everyman embeds: host postMessage AUTH delivered first, then init.
                    const t0 = Date.now();
                    const snapshotLs = () => {
                        try {
                            const active_loginid = localStorage.getItem('active_loginid') || '';
                            return {
                                auth_flow: localStorage.getItem('auth_flow'),
                                active_loginid: isEverymanAuthLogFull() ? active_loginid : undefined,
                                active_loginid_len: active_loginid.length,
                                oauth2_token_len: (localStorage.getItem('oauth2_access_token') || '').length,
                                client_accounts_rows: Object.keys(
                                    JSON.parse(localStorage.getItem('client.accounts') || '{}')
                                ).length,
                            };
                        } catch {
                            return { auth_flow: 'read_error', active_loginid_len: 0, oauth2_token_len: 0, client_accounts_rows: -1 };
                        }
                    };
                    // eslint-disable-next-line no-console
                    console.info('🖼️ [everyman-auth-verbose] iframe onAuth:start', snapshotLs());

                    try {
                        await root_store.client.init?.();
                        const client = root_store.client;
                        // eslint-disable-next-line no-console
                        console.info('🖼️ [everyman-auth-verbose] iframe onAuth:after_client.init', {
                            ms: Date.now() - t0,
                            is_logged_in: client?.is_logged_in,
                            loginid: isEverymanAuthLogFull() ? client?.loginid : undefined,
                            loginid_len: typeof client?.loginid === 'string' ? client.loginid.length : 0,
                            is_authorize: client?.is_authorize,
                            ls: snapshotLs(),
                        });
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error('🖼️ [everyman-auth-verbose] iframe onAuth:client.init threw', {
                            ms: Date.now() - t0,
                            error: e instanceof Error ? e.message : String(e),
                            ls: snapshotLs(),
                        });
                        console.error('[dtrader-c][bridge] client.init failed after AUTH:', e);
                    }
                },
                onSwitch: async account => {
                    try {
                        await root_store.client.switchAccount?.(account);
                    } catch (e) {
                        console.error('[dtrader-c][bridge] switchAccount failed:', e);
                    }
                },
                onLogout: async () => {
                    try {
                        await root_store.client.logout?.();
                    } catch (e) {
                        console.error('[dtrader-c][bridge] logout failed:', e);
                    }
                },
            });
        }

        const wrapper = document.getElementById('deriv_app');
        if (wrapper) {
            ReactDOM.render(<App useSuspense={false} root_store={root_store} />, wrapper);
        }

        if (!isBridgeMode && shouldLogout) {
            root_store.client
                .logout()
                .catch(error => {
                    console.error('[dtrader-c][index] Error processing logout query:', error);
                });
        }
    };

    initApp();
}
